import { motion } from 'framer-motion';

function SectionHeading({ title, subtitle }: { title: string; subtitle?: string }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="mb-12 md:mb-16 text-center lg:text-left"
        >
            {subtitle && <span className="block text-brand font-sans tracking-[0.2em] md:tracking-[0.3em] uppercase text-[10px] md:text-xs mb-4 font-bold">{subtitle}</span>}
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-foreground font-medium tracking-tight">
                {title}
            </h2>
        </motion.div>
    );
}

const teamMembers = [
    {
        name: 'Emilia Ivanova',
        role: 'Visionary & Chief Executive',
        desc: 'The face of an empire. Uniting absolute visual perfection with the visionary mindset that drives our entire autonomous ecosystem globally.',
        image: '/emilia.jpg',
        position: 'center 15%',
    },
    {
        name: 'Bertan Yalcintepe',
        role: 'Founder & Architect',
        desc: 'Driving the vision. Building solutions for the unthinkable when the flow states hit, turning massive problems into automated simplicity.',
        image: '/bertan-yalcintepe.jpg',
        position: 'center 15%',
    },
    {
        name: 'Tina Hillbrandt',
        role: 'Chief Technology Officer',
        desc: 'Leading the development of our core apps and global SaaS operations. Empowering our users with software that feels like magic.',
        image: '/tina-hillbrandt.jpg',
        position: 'center 35%',
    },
    {
        name: 'Christian Vogel',
        role: 'Lead Security & Data Engineer',
        desc: 'The technical guardian. Ensuring absolute data integrity and impenetrable logic across all Duyus OÜ architectures.',
        image: '/christian-vogel.jpg',
        position: 'center 15%',
    },
    {
        name: 'Maike Tomalla',
        role: 'Director of Partnerships',
        desc: 'Scaling our operations and connecting our SaaS tools with exactly the businesses and people who need their time back the most.',
        image: '/maike-tomalla.jpg',
        position: 'center 35%',
    }
];

export function TeamSection() {
    return (
        <section className="relative z-10 w-full py-20 lg:py-32 px-6 bg-[#030303] border-t border-white/5">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row gap-12 justify-between items-end mb-16">
                    <SectionHeading title="The Board" subtitle="Executive Leadership" />
                    <p className="max-w-md text-neutral-400 font-light text-sm md:text-base leading-relaxed text-center lg:text-right pb-4">
                        We aren't a cold corporate entity, and we aren't a charity. We are a passionate team of builders developing the unthinkable when the flow packs us.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6 mt-16 auto-rows-[400px]">
                    {teamMembers.map((member, i) => (
                        <motion.div
                            key={member.name}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: i * 0.1 }}
                            className="relative overflow-hidden group bg-neutral-900 border border-white/5"
                        >
                            <img
                                src={member.image}
                                alt={member.name}
                                loading="lazy"
                                width={574}
                                height={804}
                                style={{ objectPosition: member.position }}
                                className="w-full h-full object-cover opacity-60 group-hover:opacity-100 transition-all duration-700 group-hover:scale-105 grayscale group-hover:grayscale-0"
                            />
                            <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
                                <span className="text-[10px] text-brand tracking-[0.2em] uppercase block mb-1">{member.role}</span>
                                <h3 className="font-serif text-2xl text-white mb-2">{member.name}</h3>
                                <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-all duration-500 ease-in-out">
                                    <p className="overflow-hidden text-neutral-400 font-light text-xs leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100">
                                        {member.desc}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 1, delay: 0.4 }}
                    className="mt-24 md:mt-32 max-w-4xl mx-auto text-center border-t border-white/5 pt-16 md:pt-24"
                >
                    <span className="block text-brand font-sans tracking-[0.3em] uppercase text-[10px] md:text-xs mb-6 font-bold">
                        Our DNA
                    </span>
                    <h3 className="font-serif text-3xl md:text-5xl text-white mb-8 tracking-tight">Technological Sovereignty</h3>

                    <div className="space-y-6 md:space-y-8 text-neutral-400 font-light text-sm md:text-base lg:text-lg leading-relaxed">
                        <p>
                            We aren't a cold corporate machine, and we definitely aren't a charity. We are a passionate, tightly-knit team of builders who develop the unthinkable when the flow packs us. We build what we enjoy, and we engineer solutions that shift paradigms.
                        </p>
                        <p>
                            What you see here isn't just a website or a collection of tools. It is the architecture of <span className="text-white">technological sovereignty</span>. By merging high-end digital infrastructure with human creativity, we have created an autonomous ecosystem that scales itself.
                        </p>
                        <p className="text-white font-serif text-xl md:text-2xl italic pt-4">
                            "We don't just build tools. We build the architecture that dictates how the world of tomorrow communicates, works, and looks."
                        </p>
                    </div>
                </motion.div>

            </div>
        </section>
    );
}
